/**
 * OverlayControls Component
 *
 * Shared component for controlling background overlay settings
 * Used when marketing blocks have background images/videos
 *
 * Features:
 * - Enable/disable overlay
 * - Opacity slider (0-100%)
 * - Blur effect toggle and slider (0-10px)
 * - Gradient overlay option
 * - Real-time preview (when integrated with parent)
 *
 * Props:
 * - value: Overlay settings object { enabled, opacity, blur, gradient }
 * - onChange: Callback when settings change
 * - showPreview: Boolean to show live preview
 */

import React from "react";

const DEFAULT_OVERLAY = {
  enabled: false,
  opacity: 50,
  blur: 0,
  gradient: false,
};

export default function OverlayControls({
  value = DEFAULT_OVERLAY,
  onChange,
  showPreview = false,
}) {
  const overlay = { ...DEFAULT_OVERLAY, ...value };

  const handleChange = (field, fieldValue) => {
    onChange({
      ...overlay,
      [field]: fieldValue,
    });
  };

  const handleToggle = () => {
    onChange({
      ...overlay,
      enabled: !overlay.enabled,
    });
  };

  return (
    <div className="overlay-controls border rounded p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-medium text-neutral-700">
          Background Overlay
        </label>
        <button
          type="button"
          onClick={handleToggle}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
            ${overlay.enabled ? "bg-blue-600" : "bg-gray-200"}
          `}
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
              ${overlay.enabled ? "translate-x-6" : "translate-x-1"}
            `}
          />
        </button>
      </div>

      {overlay.enabled && (
        <div className="space-y-4">
          {/* Opacity slider */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-neutral-600">
                Opacity
              </label>
              <span className="text-xs text-neutral-500">
                {overlay.opacity}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={overlay.opacity}
              onChange={(e) =>
                handleChange("opacity", parseInt(e.target.value))
              }
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-neutral-400 mt-1">
              <span>Transparent</span>
              <span>Opaque</span>
            </div>
          </div>

          {/* Blur slider */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-neutral-600">
                Blur
              </label>
              <span className="text-xs text-neutral-500">{overlay.blur}px</span>
            </div>
            <input
              type="range"
              min="0"
              max="10"
              step="1"
              value={overlay.blur}
              onChange={(e) => handleChange("blur", parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-neutral-400 mt-1">
              <span>No blur</span>
              <span>Maximum</span>
            </div>
          </div>

          {/* Gradient option */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="gradient-overlay"
              checked={overlay.gradient}
              onChange={(e) => handleChange("gradient", e.target.checked)}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label
              htmlFor="gradient-overlay"
              className="ml-2 text-sm text-neutral-700"
            >
              Gradient overlay (dark to transparent)
            </label>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="mt-3 pt-3 border-t">
              <div className="text-xs font-medium text-neutral-600 mb-2">
                Preview:
              </div>
              <div
                className="h-20 rounded relative overflow-hidden"
                style={{
                  backgroundImage:
                    "url('data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h30v30H0V0zm30 30h30v30H30V30z' fill='%23ddd'/%3E%3C/svg%3E')",
                  backgroundSize: "30px 30px",
                }}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundColor: overlay.gradient
                      ? `rgba(0, 0, 0, 0)`
                      : `rgba(0, 0, 0, ${overlay.opacity / 100})`,
                    backdropFilter:
                      overlay.blur > 0 ? `blur(${overlay.blur}px)` : "none",
                    backgroundImage: overlay.gradient
                      ? `linear-gradient(to bottom, rgba(0, 0, 0, ${overlay.opacity / 100}), transparent)`
                      : "none",
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white text-sm font-medium drop-shadow-lg">
                    Sample Text
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
